import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Checkbox,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  VisibilityOff as VisibilityOffIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import DataTable from '../components/DataTable';
import { User, Column, SnackbarState, PaginationParams } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { usersService } from '../services/usersService';
import { tenantService } from '../services/tenantService';
import { subscriptionService } from '../services/subscriptionService';
import { rolesService } from '../services/rolesService';
import { useDebounce } from '../hooks/useDebounce';
import { 
  mapApiUserToUi, 
  mapUiToCreateUserApiModel, 
  mapUiToUpdateUserApiModelPatch 
} from '../utils/usersMapper';
import StatusChip from '../components/StatusChip';
import { PERMISSIONS } from '../utils/permissionAccess';

interface UserFormData {
  email: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
  subscription: string;
  role: string;
  status: 'Active' | 'Inactive';
}

const Users: React.FC = () => {
  const { tokens, login, user } = useAuth();
  
  // Get user permissions
  const userPermissions = user?.permissions || [];
  
  // Helper function to check if user has a specific permission
  const hasPermission = (permission: string): boolean => {
    return userPermissions.some(userPerm => 
      userPerm.toLowerCase() === permission.toLowerCase() ||
      userPerm === permission
    );
  };
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'success' });
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 1,
    pageSize: 10,
  });
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [sortBy, setSortBy] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [includeInactive, setIncludeInactive] = useState<boolean>(false);
  const [tenants, setTenants] = useState<any[]>([]);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [filteredRoles, setFilteredRoles] = useState<any[]>([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState<boolean>(false);
  const [rolesLoading, setRolesLoading] = useState<boolean>(false);
  const [loadingSubscriptionDetails, setLoadingSubscriptionDetails] = useState<boolean>(false);
  const [subscriptionsError, setSubscriptionsError] = useState<string | null>(null);
  const [rolesError, setRolesError] = useState<string | null>(null);

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    firstName: '',
    lastName: '',
    jobTitle: '',
    subscription: '',
    role: '',
    status: 'Active',
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const [openRoleModal, setOpenRoleModal] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newRole, setNewRole] = useState<string>('');

  // Bulk add state
  const [openBulkAddModal, setOpenBulkAddModal] = useState<boolean>(false);
  const [bulkSubscriptionId, setBulkSubscriptionId] = useState<string>('');
  const [bulkUsers, setBulkUsers] = useState<Array<{
    firstName: string;
    lastName: string;
    email: string;
    roleId: string;
  }>>([{ firstName: '', lastName: '', email: '', roleId: '' }]);
  const [bulkSendWelcomeEmail, setBulkSendWelcomeEmail] = useState<boolean>(true);
  const [bulkFilteredRoles, setBulkFilteredRoles] = useState<any[]>([]);
  const [bulkLoadingSubscriptionDetails, setBulkLoadingSubscriptionDetails] = useState<boolean>(false);
  const [bulkFormErrors, setBulkFormErrors] = useState<Record<string, string>>({});
  const [bulkSubmitting, setBulkSubmitting] = useState<boolean>(false);

  // Bulk edit state
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [openBulkChangeRoleModal, setOpenBulkChangeRoleModal] = useState<boolean>(false);
  const [bulkEditRoleId, setBulkEditRoleId] = useState<string>('');
  const [bulkEditLoading, setBulkEditLoading] = useState<boolean>(false);

  // Initialize services and load data
  useEffect(() => {
    if (tokens && login) {
      usersService.initialize(tokens, login);
      tenantService.initialize(tokens, login);
      subscriptionService.initialize(tokens, login);
      rolesService.initialize(tokens, login);
      loadUsers({ includeInactive });
      loadTenants();
      loadSubscriptions();
      loadRoles();
    }
  }, [tokens, login]);

  // Track when users state changes
  useEffect(() => {
    console.log('👥 Users state changed:', {
      count: users.length,
      users: users.map(u => ({ id: u.id, name: u.name, email: u.email }))
    });
  }, [users]);

  // Handle search term changes with debouncing
  useEffect(() => {
    console.log('🔍 Search term changed:', { 
      searchTerm, 
      debouncedSearchTerm,
      currentPage: pagination.page 
    });
    
    // Reset to page 1 when search term changes
    if (debouncedSearchTerm !== searchTerm) {
      setPagination(prev => ({ ...prev, page: 1 }));
    }
    
    // Load users with new search term
    loadUsers({ 
      ...pagination, 
      page: 1, // Always start from page 1 when searching
      searchTerm: debouncedSearchTerm,
      includeInactive
    });
  }, [debouncedSearchTerm]);

  // Handle sorting changes
  useEffect(() => {
    if (sortBy) {
      console.log('🔄 Sort changed:', { sortBy, sortDirection });
      // Reset to page 1 when sorting changes
      setPagination(prev => ({ ...prev, page: 1 }));
      // Load users with new sort parameters
      loadUsers({ 
        ...pagination, 
        page: 1, // Always start from page 1 when sorting
        searchTerm: debouncedSearchTerm,
        sortBy,
        sortDirection,
        includeInactive
      });
    }
  }, [sortBy, sortDirection]);

  // Handle includeInactive filter changes
  useEffect(() => {
    console.log('🔄 Include inactive filter changed:', includeInactive);
    // Reset to page 1 when filter changes
    setPagination(prev => ({ ...prev, page: 1 }));
    // Load users with new filter
    loadUsers({ 
      page: 1,
      pageSize: pagination.pageSize || 10,
      searchTerm: debouncedSearchTerm,
      sortBy: sortBy || undefined,
      sortDirection: sortBy ? sortDirection : undefined,
      includeInactive
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive]);

  // Effect to maintain search input focus after search operations
  useEffect(() => {
    // Only run this effect when we have search results and are not loading
    if (!loading && debouncedSearchTerm && users.length >= 0) {
      // Small delay to ensure the search input can be focused
      const timeoutId = setTimeout(() => {
        const searchInput = document.querySelector('input[placeholder="Search..."]') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
        }
      }, 150);
      
      return () => clearTimeout(timeoutId);
    }
  }, [loading, debouncedSearchTerm, users.length]);

  const loadUsers = async (params: PaginationParams = pagination) => {
    try {
      // Include sorting parameters and includeInactive in the API call
      const apiParams = {
        ...params,
        sortBy: sortBy || undefined,
        sortDirection: sortBy ? sortDirection : undefined,
        includeInactive: includeInactive
      };
      
      console.log('🚀 Loading users with params:', apiParams);
      setLoading(true);
      setError(null);
      
      // Check if users endpoints are available
      if (!usersService.isAuthenticated()) {
        setError('Authentication required. Please log in.');
        setLoading(false);
        return;
      }

      // Call the actual API endpoint
      const response = await usersService.getUsers(apiParams);
      console.log('🔍 Full API Response:', response);
      
      // Handle response "blindly" - work with any structure
      if (response && (response.succeeded === true || response.succeeded === undefined)) {
        // Extract data array from response - API returns paginated structure with items array
        const usersData = response.data?.items || response.data || response.items || response || [];
        console.log('📊 Users Data Array:', usersData);
        console.log('📊 Users Data Type:', typeof usersData, 'Is Array:', Array.isArray(usersData));
        console.log('📊 Full API Response Structure:', {
          succeeded: response.succeeded,
          data: response.data,
          dataItems: response.data?.items,
          dataLength: response.data?.items?.length
        });
        
        if (Array.isArray(usersData)) {
          console.log('📊 First User Object:', usersData[0]);
          console.log('📊 Subscription fields in first user:', {
            subscription: usersData[0]?.subscription,
            subscriptionSponsorName: usersData[0]?.subscription?.sponsorName,
            subscriptionSalesAgentName: usersData[0]?.subscription?.salesAgentName,
            organization: usersData[0]?.organization,
            jobTitle: usersData[0]?.jobTitle
          });
        }
        
        // Map API users to UI format with mocked tenant and role names
        const mappedUsers = Array.isArray(usersData) 
          ? usersData.map(user => {
              console.log('🔄 Mapping user:', user);
              const mapped = mapApiUserToUi(user);
              console.log('✅ Mapped user:', mapped);
              return mapped;
            })
          : [];
        
        console.log('🎯 Final Mapped Users:', mappedUsers);
        console.log('🔄 Setting users state with', mappedUsers.length, 'users');
        console.log('🔄 Previous users count:', users.length);
        setUsers(mappedUsers);
        console.log('✅ Users state updated - new count will be:', mappedUsers.length);
        
        // Calculate pagination info
        const currentPageSize = params.pageSize || 10;
        const currentPageNumber = params.page || 1;
        
        // Use API pagination data if available, otherwise estimate
        const apiTotalCount = response.data?.totalCount;
        const apiPageCount = response.data?.pageCount;
        
        // Update pagination state
        setPagination(prev => ({
          ...prev,
          page: currentPageNumber,
          pageSize: currentPageSize,
        }));
        
        // Set total items and pages from API response
        if (apiTotalCount !== undefined) {
          setTotalItems(apiTotalCount);
          setTotalPages(apiPageCount || Math.ceil(apiTotalCount / currentPageSize));
        } else {
          // Fallback to estimation if API doesn't provide pagination info
          const hasMoreData = mappedUsers.length === currentPageSize;
          if (hasMoreData) {
            const estimatedTotal = (currentPageNumber * currentPageSize) + 1;
            setTotalItems(estimatedTotal);
            setTotalPages(Math.ceil(estimatedTotal / currentPageSize));
          } else {
            const actualTotal = ((currentPageNumber - 1) * currentPageSize) + mappedUsers.length;
            setTotalItems(actualTotal);
            setTotalPages(currentPageNumber);
          }
        }
        
        console.log('📊 Pagination Info:', {
          currentPage: currentPageNumber,
          pageSize: currentPageSize,
          totalItems,
          totalPages,
          usersCount: mappedUsers.length,
          apiUrl: `/api/v1/users?page=${currentPageNumber}&pageSize=${currentPageSize}`,
          apiTotalCount,
          apiPageCount,
          responseData: response.data
        });
      } else {
        setError(response?.errorDescription || response?.message || 'Failed to load users');
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadTenants = async () => {
    try {
      console.log('🏢 Loading tenants from API...');
      
      const response = await tenantService.getTenants({ page: 1, pageSize: 100 });
      console.log('🏢 Tenants API Response:', response);
      console.log('🏢 Tenants API Response Structure:', {
        succeeded: response?.succeeded,
        data: response?.data,
        dataItems: (response as any)?.data?.items,
        dataLength: (response as any)?.data?.items?.length,
        errorDescription: response?.errorDescription
      });
      
      if (response && response.succeeded === true) {
        // Handle paginated response structure from API
        const tenantsData = (response as any).data?.items || [];
        console.log('🏢 Tenants Data:', tenantsData);
        
        if (Array.isArray(tenantsData)) {
          // Map API tenants to the format expected by the form
          const mappedTenants = tenantsData.map((tenant: any) => ({
            id: tenant.id,
            name: tenant.name
          }));
          console.log('🏢 Mapped Tenants:', mappedTenants);
          setTenants(mappedTenants);
        } else {
          console.warn('🏢 Tenants data is not an array:', tenantsData);
          setTenants([]);
        }
      } else {
        console.error('🏢 Failed to load tenants:', response);
        console.error('🏢 Error details:', {
          errorDescription: response?.errorDescription,
          succeeded: response?.succeeded
        });
        setTenants([]);
      }
    } catch (err) {
      console.error('🏢 Error loading tenants:', err);
      setTenants([]);
    }
  };

  const loadSubscriptions = async () => {
    try {
      console.log('📋 Loading subscriptions from API...');
      setSubscriptionsLoading(true);
      setSubscriptionsError(null);
      
      const response = await subscriptionService.getSubscriptions({ page: 1, pageSize: 100 });
      console.log('📋 Subscriptions API Response:', response);
      console.log('📋 Subscriptions API Response Structure:', {
        succeeded: response?.succeeded,
        data: response?.data,
        dataItems: (response as any)?.data?.items,
        dataLength: (response as any)?.data?.items?.length,
        errorDescription: response?.errorDescription
      });
      
      if (response && response.succeeded === true) {
        // Handle paginated response structure from API
        const subscriptionsData = (response as any).data?.items || [];
        console.log('📋 Subscriptions Data:', subscriptionsData);
        
        if (Array.isArray(subscriptionsData)) {
          // Map API subscriptions to the format expected by the form
          const mappedSubscriptions = subscriptionsData.map((subscription: any) => ({
            id: subscription.id,
            name: subscription.sponsorName || subscription.name || 'Unnamed Subscription'
          }));
          console.log('📋 Mapped Subscriptions:', mappedSubscriptions);
          setSubscriptions(mappedSubscriptions);
          setSubscriptionsError(null);
        } else {
          console.warn('📋 Subscriptions data is not an array:', subscriptionsData);
          setSubscriptions([]);
          setSubscriptionsError('Invalid data format received from API');
        }
      } else {
        console.error('📋 Failed to load subscriptions:', response);
        console.error('📋 Error details:', {
          errorDescription: response?.errorDescription,
          succeeded: response?.succeeded
        });
        setSubscriptions([]);
        setSubscriptionsError(response?.errorDescription || 'Failed to load subscriptions');
      }
    } catch (err) {
      console.error('📋 Error loading subscriptions:', err);
      console.error('📋 Error details:', {
        message: err instanceof Error ? err.message : 'Unknown error',
        stack: err instanceof Error ? err.stack : undefined
      });
      setSubscriptions([]);
      setSubscriptionsError(err instanceof Error ? err.message : 'Failed to load subscriptions');
    } finally {
      setSubscriptionsLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      console.log('🎭 Loading roles from API...');
      setRolesLoading(true);
      setRolesError(null);
      
      const response = await rolesService.getRoles({ page: 1, pageSize: 200 });
      console.log('🎭 Roles API Response:', response);
      console.log('🎭 Roles API Response Structure:', {
        succeeded: response?.succeeded,
        data: response?.data,
        dataItems: (response as any)?.data?.items,
        dataLength: (response as any)?.data?.items?.length,
        errorDescription: response?.errorDescription
      });
      
      if (response && response.succeeded === true) {
        // Handle paginated response structure from API
        const rolesData = (response as any).data?.items || [];
        console.log('🎭 Roles Data:', rolesData);
        
        if (Array.isArray(rolesData)) {
          // Store full role objects (with tenants array) for filtering
          setRoles(rolesData);
          setRolesError(null);
        } else {
          console.warn('🎭 Roles data is not an array:', rolesData);
          setRoles([]);
          setRolesError('Invalid data format received from API');
        }
      } else {
        console.error('🎭 Failed to load roles:', response);
        console.error('🎭 Error details:', {
          errorDescription: response?.errorDescription,
          succeeded: response?.succeeded
        });
        setRoles([]);
        setRolesError(response?.errorDescription || 'Failed to load roles');
      }
    } catch (err) {
      console.error('🎭 Error loading roles:', err);
      console.error('🎭 Error details:', {
        message: err instanceof Error ? err.message : 'Unknown error',
        stack: err instanceof Error ? err.stack : undefined
      });
      setRoles([]);
      setRolesError(err instanceof Error ? err.message : 'Failed to load roles');
    } finally {
      setRolesLoading(false);
    }
  };

  // Handle subscription change - fetch subscription details and filter roles
  const handleSubscriptionChange = async (subscriptionId: string): Promise<any[]> => {
    setFormData(prev => ({ ...prev, subscription: subscriptionId, role: '' })); // Clear role when subscription changes
    
    if (!subscriptionId) {
      setFilteredRoles([]);
      return [];
    }

    try {
      setLoadingSubscriptionDetails(true);
      console.log('📋 Fetching subscription details for:', subscriptionId);
      
      const response = await subscriptionService.getSubscriptionById(subscriptionId);
      
      if (response && response.succeeded && response.data) {
        const subscription = response.data;
        const tenantId = subscription.tenantId;
        
        console.log('📋 Subscription tenantId:', tenantId);
        console.log('🎭 All roles:', roles);
        
        // Filter roles that have this tenantId in their tenants array
        const filtered = roles.filter((role: any) => {
          const roleTenants = role.tenants || [];
          const hasTenant = roleTenants.some((tenant: any) => {
            // Handle both { id: string, name: string } objects and direct string IDs
            const tenantIdToCheck = typeof tenant === 'object' ? tenant.id : tenant;
            return tenantIdToCheck === tenantId;
          });
          console.log(`🎭 Role "${role.name}" has tenant ${tenantId}:`, hasTenant, 'tenants:', roleTenants);
          return hasTenant;
        });
        
        console.log('🎭 Filtered roles:', filtered);
        
        // Map to format expected by dropdown
        const mappedFilteredRoles = filtered.map((role: any) => ({
          id: role.id,
          name: role.name
        }));
        
        setFilteredRoles(mappedFilteredRoles);
        return mappedFilteredRoles;
      } else {
        console.error('📋 Failed to fetch subscription details:', response);
        setFilteredRoles([]);
        return [];
      }
    } catch (err) {
      console.error('📋 Error fetching subscription details:', err);
      setFilteredRoles([]);
      return [];
    } finally {
      setLoadingSubscriptionDetails(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Required field validations
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    // Check if subscription data is available
    if (subscriptionsError) {
      errors.subscription = `Cannot load subscriptions: ${subscriptionsError}`;
    } else if (subscriptions.length === 0 && !subscriptionsLoading) {
      errors.subscription = 'No subscriptions available. Please try refreshing the page.';
    } else if (!formData.subscription) {
      errors.subscription = 'Subscription is required';
    }

    // Check if roles data is available
    if (rolesError) {
      errors.role = `Cannot load roles: ${rolesError}`;
    } else if (roles.length === 0 && !rolesLoading) {
      errors.role = 'No roles available. Please try refreshing the page.';
    } else if (!formData.role) {
      errors.role = 'Role is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getTenantName = (subscriptionName: string): string => {
    // The subscription name is already provided by the API
    return subscriptionName;
  };

  // Removed unused helper getTenantIdByName

  const getSubscriptionIdByName = (subscriptionName: string): string => {
    // Find subscription ID by matching the subscription name
    const subscription = subscriptions.find(s => s.name === subscriptionName);
    return subscription ? subscription.id : '';
  };

  const columns: Column[] = [
    { id: 'email', label: 'Email', minWidth: 200, width: '25%', sortable: true },
    { 
      id: 'name', 
      label: 'Name', 
      minWidth: 200, 
      width: '20%', 
      sortable: true,
      render: (value: string) => value || 'N/A'
    },
    { 
      id: 'jobTitle', 
      label: 'Job Title', 
      minWidth: 150, 
      width: '15%', 
      sortable: true,
      render: (value: string) => value || 'N/A'
    },
    { 
      id: 'tenant', 
      label: 'Subscription', 
      minWidth: 150, 
      width: '15%', 
      sortable: true,
      render: (value: string) => getTenantName(value)
    },
    { 
      id: 'roles', 
      label: 'Roles', 
      minWidth: 120, 
      width: '10%', 
      sortable: false,
      render: (value: string[]) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {value.map((roleName) => (
            <Chip key={roleName} label={roleName} size="small" />
          ))}
        </Box>
      )
    },
    { 
      id: 'status', 
      label: 'Status', 
      minWidth: 80, 
      width: '10%', 
      sortable: true,
      render: (value: string) => <StatusChip status={value as 'Active' | 'Inactive'} />
    },
  ];

  const handleAdd = (): void => {
    console.log('➕ Opening create user modal');
    console.log('🏢 Available tenants:', tenants);
    console.log('📋 Available subscriptions:', subscriptions);
    console.log('🎭 Available roles:', roles);
    setEditingUser(null);
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      jobTitle: '',
      subscription: '',
      role: '',
      status: 'Active',
    });
    setFilteredRoles([]); // Clear filtered roles when opening Add modal
    setFormErrors({}); // Clear any previous errors
    setOpenModal(true);
  };

  const handleBulkAdd = (): void => {
    console.log('➕ Opening bulk add users modal');
    setBulkSubscriptionId('');
    setBulkUsers([{ firstName: '', lastName: '', email: '', roleId: '' }]);
    setBulkSendWelcomeEmail(true);
    setBulkFilteredRoles([]);
    setBulkFormErrors({});
    setOpenBulkAddModal(true);
  };

  const handleBulkSubscriptionChange = async (subscriptionId: string): Promise<void> => {
    setBulkSubscriptionId(subscriptionId);
    setBulkUsers(prev => prev.map(user => ({ ...user, roleId: '' }))); // Clear role selections
    
    if (!subscriptionId) {
      setBulkFilteredRoles([]);
      return;
    }

    try {
      setBulkLoadingSubscriptionDetails(true);
      console.log('📋 Fetching subscription details for bulk add:', subscriptionId);
      
      // Fetch subscription details to get tenantId
      const subscriptionResponse = await subscriptionService.getSubscriptionById(subscriptionId);
      
      if (subscriptionResponse && subscriptionResponse.succeeded && subscriptionResponse.data) {
        const subscription = subscriptionResponse.data;
        const tenantId = subscription.tenantId;
        
        console.log('📋 Subscription tenantId:', tenantId);
        
        // Fetch all roles from the roles endpoint
        console.log('🎭 Fetching roles from endpoint...');
        const rolesResponse = await rolesService.getRoles({ page: 1, pageSize: 200 });
        
        if (rolesResponse && rolesResponse.succeeded) {
          const rolesData = (rolesResponse as any).data?.items || [];
          console.log('🎭 Fetched roles from endpoint:', rolesData.length);
          
          // Filter roles that have this tenantId in their tenants array
          const filtered = rolesData.filter((role: any) => {
            const roleTenants = role.tenants || [];
            const hasTenant = roleTenants.some((tenant: any) => {
              const tenantIdToCheck = typeof tenant === 'object' ? tenant.id : tenant;
              return tenantIdToCheck === tenantId;
            });
            console.log(`🎭 Role "${role.name}" has tenant ${tenantId}:`, hasTenant);
            return hasTenant;
          });
          
          console.log('🎭 Filtered roles for subscription:', filtered.length);
          
          // Map to format expected by dropdown and store in state
          const mappedFilteredRoles = filtered.map((role: any) => ({
            id: role.id,
            name: role.name
          }));
          
          setBulkFilteredRoles(mappedFilteredRoles);
        } else {
          console.error('🎭 Failed to fetch roles:', rolesResponse);
          setBulkFilteredRoles([]);
        }
      } else {
        console.error('📋 Failed to fetch subscription details:', subscriptionResponse);
        setBulkFilteredRoles([]);
      }
    } catch (err) {
      console.error('📋 Error fetching subscription details or roles:', err);
      setBulkFilteredRoles([]);
    } finally {
      setBulkLoadingSubscriptionDetails(false);
    }
  };

  const handleAddBulkUserRow = (): void => {
    setBulkUsers([...bulkUsers, { firstName: '', lastName: '', email: '', roleId: '' }]);
  };

  const handleRemoveBulkUserRow = (index: number): void => {
    if (bulkUsers.length > 1) {
      setBulkUsers(bulkUsers.filter((_, i) => i !== index));
    }
  };

  const handleClearBulkTable = (): void => {
    setBulkUsers([{ firstName: '', lastName: '', email: '', roleId: '' }]);
    setBulkFormErrors({});
  };

  const handleBulkUserChange = (index: number, field: string, value: string): void => {
    setBulkUsers(bulkUsers.map((user, i) => 
      i === index ? { ...user, [field]: value } : user
    ));
    
    // Clear error for this field when user starts typing
    if (bulkFormErrors[`${field}_${index}`]) {
      setBulkFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`${field}_${index}`];
        return newErrors;
      });
    }
  };

  // Enhanced email validation regex
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return emailRegex.test(email.trim());
  };

  const validateBulkForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Validate subscription
    if (!bulkSubscriptionId || !bulkSubscriptionId.trim()) {
      errors.subscription = 'Subscription is required';
    }

    // Validate each user row
    bulkUsers.forEach((user, index) => {
      // Validate email
      const emailTrimmed = user.email.trim();
      if (!emailTrimmed) {
        errors[`email_${index}`] = 'Email is required';
      } else if (!isValidEmail(emailTrimmed)) {
        errors[`email_${index}`] = 'Please enter a valid email address (e.g., user@example.com)';
      }

      // Validate first name
      const firstNameTrimmed = user.firstName.trim();
      if (!firstNameTrimmed) {
        errors[`firstName_${index}`] = 'First name is required';
      } else if (firstNameTrimmed.length < 2) {
        errors[`firstName_${index}`] = 'First name must be at least 2 characters';
      }

      // Validate last name
      const lastNameTrimmed = user.lastName.trim();
      if (!lastNameTrimmed) {
        errors[`lastName_${index}`] = 'Last name is required';
      } else if (lastNameTrimmed.length < 2) {
        errors[`lastName_${index}`] = 'Last name must be at least 2 characters';
      }

      // Validate role
      if (!user.roleId || !user.roleId.trim()) {
        errors[`role_${index}`] = 'Role is required';
      }
    });

    setBulkFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBulkSave = async (): Promise<void> => {
    // Validate the form first
    if (!validateBulkForm()) {
      const errorCount = Object.keys(bulkFormErrors).length;
      showSnackbar(
        `Please fix ${errorCount} validation error${errorCount > 1 ? 's' : ''} before saving`,
        'error'
      );
      return;
    }

    // Filter out completely empty rows (all fields empty)
    const validUsers = bulkUsers.filter(user => 
      user.email.trim() || user.firstName.trim() || user.lastName.trim() || user.roleId
    );

    if (validUsers.length === 0) {
      showSnackbar('Please add at least one user with all required fields', 'error');
      return;
    }

    // Double-check that all valid users have all required fields filled
    const incompleteUsers = validUsers.filter(user => 
      !user.email.trim() || !user.firstName.trim() || !user.lastName.trim() || !user.roleId
    );

    if (incompleteUsers.length > 0) {
      showSnackbar(
        `Please fill in all required fields for ${incompleteUsers.length} user${incompleteUsers.length > 1 ? 's' : ''}`,
        'error'
      );
      return;
    }

    // Validate emails one more time before submission
    const invalidEmails = validUsers.filter(user => !isValidEmail(user.email.trim()));
    if (invalidEmails.length > 0) {
      showSnackbar(
        `Please fix invalid email${invalidEmails.length > 1 ? 's' : ''} before saving`,
        'error'
      );
      return;
    }

    try {
      setBulkSubmitting(true);
      console.log('💾 Bulk adding users:', {
        subscriptionId: bulkSubscriptionId,
        users: validUsers,
        sendWelcomeEmail: bulkSendWelcomeEmail
      });

      const response = await usersService.bulkAddUsers({
        subscriptionId: bulkSubscriptionId,
        users: validUsers.map(user => ({
          firstName: user.firstName.trim(),
          lastName: user.lastName.trim(),
          email: user.email.trim(),
          roleId: user.roleId
        })),
        sendWelcomeEmail: bulkSendWelcomeEmail
      });

      if (!response || !response.succeeded) {
        showSnackbar(response?.errorDescription || 'Failed to bulk add users', 'error');
        return;
      }

      showSnackbar(`Successfully added ${validUsers.length} user(s)`, 'success');
      setOpenBulkAddModal(false);
      
      // Reload users to show the newly added ones
      loadUsers({ includeInactive });
    } catch (err) {
      console.error('Error bulk adding users:', err);
      showSnackbar('Failed to bulk add users', 'error');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleBulkCancel = (): void => {
    setOpenBulkAddModal(false);
  };

  // Bulk edit handlers
  const handleBulkUpdate = async (updates: Array<{
    userId: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    roleId?: string;
    isActive?: boolean;
    isBlocked?: boolean;
  }>): Promise<void> => {
    try {
      setBulkEditLoading(true);
      console.log('💾 Bulk updating users:', updates);

      const response = await usersService.bulkUpdateUsers({ users: updates });

      if (!response || !response.succeeded) {
        showSnackbar(response?.errorDescription || 'Failed to bulk update users', 'error');
        return;
      }

      // Handle response - response.data should contain array of results
      // Each result should have: userId, succeeded, errorDescription/message
      const results = response.data || [];
      const successfulIds: string[] = [];
      const failedUpdates: Array<{ userId: string; error: string }> = [];

      // Process each result from the response
      results.forEach((result: any) => {
        const userId = result.userId || result.id;
        if (!userId) return;

        if (result.succeeded !== false && !result.errorDescription && !result.message && !result.error) {
          // Success case
          successfulIds.push(userId);
        } else {
          // Error case
          failedUpdates.push({
            userId,
            error: result.errorDescription || result.message || result.error || 'Unknown error'
          });
        }
      });

      // Unselect successful rows immediately
      setSelectedUserIds(prev => prev.filter(id => !successfulIds.includes(id)));

      // Show error messages for failed rows (they remain selected)
      failedUpdates.forEach(({ userId, error }) => {
        const user = users.find(u => u.id === userId);
        showSnackbar(
          `Failed to update ${user?.email || userId}: ${error}`,
          'error'
        );
      });

      // Show success message and reload users to reflect changes
      if (successfulIds.length > 0) {
        showSnackbar(
          `Successfully updated ${successfulIds.length} user${successfulIds.length > 1 ? 's' : ''}`,
          'success'
        );
        
        // Reload users with current pagination and search params to update fields
        loadUsers({
          page: pagination.page || 1,
          pageSize: pagination.pageSize || 10,
          searchTerm: debouncedSearchTerm,
          sortBy,
          sortDirection,
          includeInactive
        });
      }
    } catch (err) {
      console.error('Error bulk updating users:', err);
      showSnackbar('Failed to bulk update users', 'error');
    } finally {
      setBulkEditLoading(false);
    }
  };

  const handleBulkChangeRole = async (): Promise<void> => {
    if (!bulkEditRoleId || selectedUserIds.length === 0) {
      showSnackbar('Please select a role', 'error');
      return;
    }

    const selectedUsers = users.filter(u => selectedUserIds.includes(u.id));
    
    // Only send roleId for users whose role is actually changing
    const updates = selectedUsers
      .map(user => {
        const userRoleName = user.roles[0] || '';
        const currentRole = roles.find(r => r.name === userRoleName);
        const currentRoleId = currentRole?.id || '';

        // Only include if role is actually changing
        if (currentRoleId !== bulkEditRoleId) {
          return {
            userId: user.id,
            roleId: bulkEditRoleId
          };
        }
        return null;
      })
      .filter((update): update is { userId: string; roleId: string } => update !== null);

    if (updates.length === 0) {
      showSnackbar('No users have a different role to change', 'info');
      setOpenBulkChangeRoleModal(false);
      setBulkEditRoleId('');
      return;
    }

    await handleBulkUpdate(updates);
    setOpenBulkChangeRoleModal(false);
    setBulkEditRoleId('');
  };

  const handleBulkDisable = async (): Promise<void> => {
    if (selectedUserIds.length === 0) {
      return;
    }

    const selectedUsers = users.filter(u => selectedUserIds.includes(u.id));
    
    // Only send isActive: false for users that are currently active
    const updates = selectedUsers
      .filter(user => user.status === 'Active')
      .map(user => ({
        userId: user.id,
        isActive: false
      }));

    if (updates.length === 0) {
      showSnackbar('All selected users are already disabled', 'info');
      return;
    }

    await handleBulkUpdate(updates);
  };

  const handleBulkDelete = async (): Promise<void> => {
    if (selectedUserIds.length === 0) {
      return;
    }

    const selectedUsers = users.filter(u => selectedUserIds.includes(u.id));
    
    // Only send isActive: false and isBlocked: true for users that aren't already blocked/inactive
    // Build update object with only changed fields
    const updates = selectedUsers
      .map(user => {
        const update: {
          userId: string;
          isActive?: boolean;
          isBlocked?: boolean;
        } = {
          userId: user.id
        };

        // Only include isActive if user is currently active
        if (user.status === 'Active') {
          update.isActive = false;
        }

        // Always set isBlocked to true (assuming this is the delete action)
        update.isBlocked = true;

        return update;
      })
      .filter(update => Object.keys(update).length > 1); // Filter out if only userId (shouldn't happen but safety check)

    if (updates.length === 0) {
      showSnackbar('No users to delete', 'info');
      return;
    }

    await handleBulkUpdate(updates);
  };

  const handleEdit = async (user: User): Promise<void> => {
    console.log('✏️ Opening edit user modal for:', user);
    console.log('🏢 Available tenants:', tenants);
    console.log('📋 Available subscriptions:', subscriptions);
    console.log('🎭 Available roles:', roles);
    setEditingUser(user);
    // Split the combined name back into firstName and lastName
    const nameParts = user.name.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    
    const subscriptionId = getSubscriptionIdByName(user.tenant);
    
    setFormData({
      email: user.email,
      firstName,
      lastName,
      jobTitle: user.jobTitle,
      subscription: subscriptionId, // Convert tenant name to subscription ID
      role: '', // Will be set after roles are filtered
      status: user.status,
    });
    
    // If subscription is found, load filtered roles
    if (subscriptionId) {
      const filteredRolesList = await handleSubscriptionChange(subscriptionId);
      
      // After roles are filtered, find and set the current role
      const userRoleName = user.roles[0] || '';
      const matchingRole = filteredRolesList.find((role: any) => role.name === userRoleName);
      const roleId = matchingRole?.id || '';
      
      console.log('🎭 User role name:', userRoleName);
      console.log('🎭 Matching role:', matchingRole);
      console.log('🎭 Role ID:', roleId);
      
      if (roleId) {
        setFormData(prev => ({ ...prev, role: roleId }));
      }
    } else {
      // Fallback: try to find role from all roles if subscription not found
      const userRoleName = user.roles[0] || '';
      const matchingRole = roles.find((role: any) => role.name === userRoleName);
      const roleId = matchingRole?.id || '';
      setFormData(prev => ({ ...prev, role: roleId }));
    }
    
    setFormErrors({}); // Clear any previous errors
    setOpenModal(true);
  };

  const handleDelete = async (user: User): Promise<void> => {
    try {
      await usersService.deleteUser(user.id);
      setUsers(users.filter(u => u.id !== user.id));
      showSnackbar('User deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting user:', err);
      showSnackbar('Failed to delete user', 'error');
    }
  };

  const handleToggleStatus = async (user: User): Promise<void> => {
    try {
      const newStatus = user.status === 'Active' ? 'Inactive' : 'Active';
      await usersService.toggleUserStatus(user.id, newStatus === 'Active');
      
      setUsers(users.map(u => 
        u.id === user.id 
          ? { ...u, status: newStatus }
          : u
      ));
      showSnackbar(`User ${newStatus.toLowerCase()} successfully`, 'success');
    } catch (err) {
      console.error('Error toggling user status:', err);
      showSnackbar('Failed to update user status', 'error');
    }
  };


  const handleChangeRole = (user: User): void => {
    console.log('🎭 Opening role change modal for user:', user);
    console.log('🎭 Available roles:', roles);
    console.log('🎭 User current roles (names):', user.roles);
    
    setSelectedUser(user);
    
    // Find the role ID that matches the user's current role name
    const userRoleName = user.roles[0] || '';
    const matchingRole = roles.find(role => role.name === userRoleName);
    const roleId = matchingRole?.id || '';
    
    console.log('🎭 Matching role for name "' + userRoleName + '":', matchingRole);
    console.log('🎭 Setting newRole to ID:', roleId);
    
    setNewRole(roleId);
    setOpenRoleModal(true);
  };

  const handleSave = async (): Promise<void> => {
    // Validate form before proceeding
    if (!validateForm()) {
      showSnackbar('Please fix the validation errors before saving', 'error');
      return;
    }

    console.log('💾 Saving user with form data:', formData);

    try {
      if (editingUser) {
        // Update existing user
        const updateData = mapUiToUpdateUserApiModelPatch(formData, editingUser);
        const response = await usersService.updateUser(editingUser.id, updateData);
        
        if (!response || !response.succeeded) {
          showSnackbar(response?.errorDescription || 'Failed to update user', 'error');
          return;
        }
        
        // Instead of relying on API response (which may be incomplete for PATCH),
        // update local state by merging formData changes with existing user data
        const updatedUser: User = {
          ...editingUser,
          email: formData.email,
          name: `${formData.firstName} ${formData.lastName}`.trim(),
          jobTitle: formData.jobTitle,
          status: formData.status,
          updatedAt: new Date(),
        };
        
        // Update role name if role was changed
        if (formData.role) {
          const selectedRole = roles.find(r => r.id === formData.role);
          if (selectedRole) {
            updatedUser.roles = [selectedRole.name];
          }
        }
        
        // Update subscription/tenant if changed
        if (formData.subscription) {
          const selectedSubscription = subscriptions.find(s => s.id === formData.subscription);
          if (selectedSubscription) {
            // Subscription name is stored in the 'name' field in the subscriptions array
            updatedUser.tenant = selectedSubscription.name || updatedUser.tenant;
          }
        }
        
        // Update local state
        setUsers(users.map(u => 
          u.id === editingUser.id ? updatedUser : u
        ));
        showSnackbar('User updated successfully', 'success');
      } else {
        // Create new user
        const createData = mapUiToCreateUserApiModel(formData);
        const response = await usersService.createUser(createData);
        
        if (!response || !response.succeeded) {
          showSnackbar(response?.errorDescription || 'Failed to create user', 'error');
          return;
        }
        
        // Handle response "blindly" - extract user data from any structure
        const newUserData = response.data || response.user || response || {};
        const mappedUser = mapApiUserToUi(newUserData);
        
        // Add to local state
        setUsers([...users, mappedUser]);
        showSnackbar('User created successfully', 'success');
      }
      setFormErrors({}); // Clear errors on successful save
      setOpenModal(false);
    } catch (err) {
      console.error('Error saving user:', err);
      showSnackbar('Failed to save user', 'error');
    }
  };

  const handleCancel = (): void => {
    setOpenModal(false);
  };


  const handleRoleChange = async (): Promise<void> => {
    try {
      if (selectedUser) {
        console.log('🎭 Changing role for user:', selectedUser.id, 'to roleId:', newRole);
        console.log('🎭 PATCH request payload:', { roleId: newRole });
        // Update user role with single roleId
        await usersService.updateUser(selectedUser.id, { roleId: newRole });
        
        // Find the role name from the ID for updating local state and success message
        const updatedRoleName = roles.find(r => r.id === newRole)?.name || newRole;
        
        // Update local state with the role name (roles array stores names, not IDs)
        setUsers(users.map(u => 
          u.id === selectedUser.id 
            ? { ...u, roles: [updatedRoleName] }
            : u
        ));
        
        showSnackbar(`Role updated to ${updatedRoleName} for ${selectedUser.email}`, 'success');
        setOpenRoleModal(false);
      }
    } catch (err) {
      console.error('Error changing role:', err);
      showSnackbar('Failed to change role', 'error');
    }
  };

  const handleCloseSnackbar = (): void => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Pagination handlers
  const handlePageChange = (newPage: number): void => {
    const currentPageSize = pagination.pageSize || 10;
    console.log('🔄 Page Change:', { from: pagination.page, to: newPage, pageSize: currentPageSize });
    setPagination(prev => ({ ...prev, page: newPage }));
    loadUsers({ page: newPage, pageSize: currentPageSize, searchTerm: debouncedSearchTerm, includeInactive });
  };

  const handlePageSizeChange = (newPageSize: number): void => {
    console.log('📏 Page Size Change:', { from: pagination.pageSize, to: newPageSize });
    console.log('📏 Current users before change:', users.length);
    setPagination(prev => ({ ...prev, pageSize: newPageSize, page: 1 }));
    console.log('📏 Calling loadUsers with new params:', { page: 1, pageSize: newPageSize });
    loadUsers({ page: 1, pageSize: newPageSize, searchTerm: debouncedSearchTerm, includeInactive });
  };

  const handleSearchChange = useCallback((newSearchTerm: string): void => {
    console.log('🔍 Search input changed:', { from: searchTerm, to: newSearchTerm });
    setSearchTerm(newSearchTerm);
  }, [searchTerm]);

  const handleSortChange = useCallback((newSortBy: string, newSortDirection: 'asc' | 'desc'): void => {
    console.log('🔄 Sort change requested:', { from: { sortBy, sortDirection }, to: { newSortBy, newSortDirection } });
    setSortBy(newSortBy);
    setSortDirection(newSortDirection);
  }, [sortBy, sortDirection]);

  // Next/Previous page handlers not used by DataTable; rely on onPageChange

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100%' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => loadUsers({ includeInactive })}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          Manage Users
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {hasPermission(PERMISSIONS.ADD_USER) && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<PersonAddIcon />}
              onClick={handleBulkAdd}
              sx={{ 
                px: 3,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-1px)',
                  transition: 'all 0.2s ease-in-out',
                },
              }}
            >
              Bulk Add Users
            </Button>
          )}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              px: 2,
              py: 1,
              borderRadius: 2,
              border: '1px solid',
              borderColor: includeInactive ? 'primary.main' : 'divider',
              bgcolor: includeInactive ? 'action.selected' : 'background.paper',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: 1,
              },
            }}
          >
            <FilterListIcon 
              sx={{ 
                fontSize: 20, 
                color: includeInactive ? 'primary.main' : 'text.secondary' 
              }} 
            />
            <FormControlLabel
              control={
                <Switch
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                  color="primary"
                  size="medium"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {includeInactive ? (
                    <VisibilityIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                  ) : (
                    <VisibilityOffIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  )}
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: includeInactive ? 600 : 400,
                      color: includeInactive ? 'primary.main' : 'text.primary',
                      ml: 0.5,
                    }}
                  >
                    {includeInactive ? 'Show disabled Users' : 'Disabled users not displayed'}
                  </Typography>
                </Box>
              }
              sx={{ m: 0 }}
            />
          </Box>
        </Box>
      </Box>
      
      {/* Bulk Edit Options Bar */}
      {selectedUserIds.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body1">
              {selectedUserIds.length} user{selectedUserIds.length > 1 ? 's' : ''} selected
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {hasPermission(PERMISSIONS.EDIT_USER) && (
                <>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => setOpenBulkChangeRoleModal(true)}
                    disabled={bulkEditLoading}
                  >
                    Change Roles
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handleBulkDisable}
                    disabled={bulkEditLoading}
                  >
                    Disable
                  </Button>
                </>
              )}
              {/* Bulk delete temporarily hidden - API not available yet */}
              {/* {hasPermission(PERMISSIONS.DELETE_USER) && (
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleBulkDelete}
                  disabled={bulkEditLoading}
                >
                  Delete
                </Button>
              )} */}
              <Button
                variant="outlined"
                onClick={() => setSelectedUserIds([])}
                disabled={bulkEditLoading}
                sx={{ color: 'white', borderColor: 'white', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
              >
                Clear Selection
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      <DataTable
        key={`users-${pagination.page}-${pagination.pageSize}-${debouncedSearchTerm}`}
        data={users}
        columns={columns}
        onAdd={hasPermission(PERMISSIONS.ADD_USER) ? handleAdd : undefined}
        onEdit={hasPermission(PERMISSIONS.EDIT_USER) ? handleEdit : undefined}
        onDelete={hasPermission(PERMISSIONS.DELETE_USER) ? handleDelete : undefined}
        onToggleStatus={hasPermission(PERMISSIONS.EDIT_USER) ? handleToggleStatus : undefined}
        onChangeRole={hasPermission(PERMISSIONS.EDIT_USER) ? handleChangeRole : undefined}
        title="Users"
        searchFields={['email', 'name']}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        selectable={true}
        selectedRows={selectedUserIds}
        onSelectionChange={setSelectedUserIds}
        pagination={{
          page: pagination.page || 1,
          pageSize: pagination.pageSize || 10,
          totalItems,
          totalPages,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange,
          onSortChange: handleSortChange,
        }}
      />

      {/* Add/Edit User Modal */}
      <Dialog open={openModal} onClose={handleCancel} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
              required
              error={!!formErrors.email}
              helperText={formErrors.email}
            />
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                fullWidth
                required
                error={!!formErrors.firstName}
                helperText={formErrors.firstName}
              />
              
              <TextField
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                fullWidth
                required
                error={!!formErrors.lastName}
                helperText={formErrors.lastName}
              />
            </Box>
            
            
            <FormControl fullWidth error={!!formErrors.subscription}>
              <InputLabel>Subscription</InputLabel>
              <Select
                value={formData.subscription}
                onChange={(e) => handleSubscriptionChange(e.target.value)}
                label="Subscription"
                required
                disabled={!!editingUser || subscriptionsLoading || (subscriptions.length === 0 && !subscriptionsError)}
              >
                {subscriptionsLoading ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        Loading subscriptions...
                      </Typography>
                    </Box>
                  </MenuItem>
                ) : subscriptionsError ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                      <Typography variant="body2" color="error">
                        Failed to load subscriptions
                      </Typography>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        onClick={(e) => {
                          e.stopPropagation();
                          loadSubscriptions();
                        }}
                      >
                        Retry
                      </Button>
                    </Box>
                  </MenuItem>
                ) : subscriptions.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      No subscriptions available
                    </Typography>
                  </MenuItem>
                ) : (
                  subscriptions.map((subscription) => (
                    <MenuItem key={subscription.id} value={subscription.id}>
                      {subscription.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {formErrors.subscription && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {formErrors.subscription}
                </Typography>
              )}
            </FormControl>
            
            {/* Only show role field in Add mode if subscription is selected, always show in Edit mode */}
            {(editingUser || formData.subscription) && (
              <FormControl fullWidth error={!!formErrors.role}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  label="Role"
                  required
                  disabled={
                    rolesLoading || 
                    loadingSubscriptionDetails ||
                    (!editingUser && !formData.subscription) ||
                    (filteredRoles.length === 0 && !rolesError && !loadingSubscriptionDetails)
                  }
                >
                  {loadingSubscriptionDetails ? (
                    <MenuItem disabled>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">
                          Loading roles for subscription...
                        </Typography>
                      </Box>
                    </MenuItem>
                  ) : rolesLoading ? (
                    <MenuItem disabled>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">
                          Loading roles...
                        </Typography>
                      </Box>
                    </MenuItem>
                  ) : rolesError ? (
                    <MenuItem disabled>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                        <Typography variant="body2" color="error">
                          Failed to load roles
                        </Typography>
                        <Button 
                          size="small" 
                          variant="outlined" 
                          onClick={(e) => {
                            e.stopPropagation();
                            loadRoles();
                          }}
                        >
                          Retry
                        </Button>
                      </Box>
                    </MenuItem>
                  ) : !editingUser && !formData.subscription ? (
                    <MenuItem disabled>
                      <Typography variant="body2" color="text.secondary">
                        Please select a subscription first
                      </Typography>
                    </MenuItem>
                  ) : filteredRoles.length === 0 ? (
                    <MenuItem disabled>
                      <Typography variant="body2" color="text.secondary">
                        No roles available for this subscription
                      </Typography>
                    </MenuItem>
                  ) : (
                    filteredRoles.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {formErrors.role && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {formErrors.role}
                  </Typography>
                )}
              </FormControl>
            )}
            
            <TextField
              label="Job Title"
              value={formData.jobTitle}
              onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
              fullWidth
            />
            
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as 'Active' | 'Inactive' })}
                label="Status"
                renderValue={(value) => <StatusChip status={value as 'Active' | 'Inactive'} />}
              >
                <MenuItem value="Active">
                  <StatusChip status="Active" />
                </MenuItem>
                <MenuItem value="Inactive">
                  <StatusChip status="Inactive" />
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>


      {/* Role Change Modal */}
      <Dialog open={openRoleModal} onClose={() => setOpenRoleModal(false)}>
        <DialogTitle>Change Role</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Change role for {selectedUser?.email}
            </Typography>
            <FormControl fullWidth>
              <InputLabel>New Role</InputLabel>
              <Select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                label="New Role"
                required
                disabled={rolesLoading || (roles.length === 0 && !rolesError)}
              >
                {rolesLoading ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        Loading roles...
                      </Typography>
                    </Box>
                  </MenuItem>
                ) : rolesError ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                      <Typography variant="body2" color="error">
                        Failed to load roles
                      </Typography>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        onClick={(e) => {
                          e.stopPropagation();
                          loadRoles();
                        }}
                      >
                        Retry
                      </Button>
                    </Box>
                  </MenuItem>
                ) : roles.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      No roles available
                    </Typography>
                  </MenuItem>
                ) : (
                  roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRoleModal(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleRoleChange} variant="contained" color="primary">
            Change Role
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Change Role Modal */}
      <Dialog open={openBulkChangeRoleModal} onClose={() => setOpenBulkChangeRoleModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Change Role</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Change role for {selectedUserIds.length} selected user{selectedUserIds.length > 1 ? 's' : ''}
            </Typography>
            <FormControl fullWidth>
              <InputLabel>New Role</InputLabel>
              <Select
                value={bulkEditRoleId}
                onChange={(e) => setBulkEditRoleId(e.target.value)}
                label="New Role"
                required
                disabled={rolesLoading || (roles.length === 0 && !rolesError)}
              >
                {rolesLoading ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        Loading roles...
                      </Typography>
                    </Box>
                  </MenuItem>
                ) : rolesError ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                      <Typography variant="body2" color="error">
                        Failed to load roles
                      </Typography>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        onClick={(e) => {
                          e.stopPropagation();
                          loadRoles();
                        }}
                      >
                        Retry
                      </Button>
                    </Box>
                  </MenuItem>
                ) : roles.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      No roles available
                    </Typography>
                  </MenuItem>
                ) : (
                  roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBulkChangeRoleModal(false)} color="secondary" disabled={bulkEditLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleBulkChangeRole} 
            variant="contained" 
            color="primary"
            disabled={bulkEditLoading || !bulkEditRoleId}
          >
            {bulkEditLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Updating...
              </Box>
            ) : (
              'Change Role'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Add Users Modal */}
      <Dialog open={openBulkAddModal} onClose={handleBulkCancel} maxWidth="lg" fullWidth>
        <DialogTitle>Bulk Add Users</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            <FormControl fullWidth error={!!bulkFormErrors.subscription} required>
              <InputLabel>Subscription</InputLabel>
              <Select
                value={bulkSubscriptionId}
                onChange={(e) => {
                  handleBulkSubscriptionChange(e.target.value);
                  // Clear subscription error when user selects one
                  if (bulkFormErrors.subscription) {
                    setBulkFormErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.subscription;
                      return newErrors;
                    });
                  }
                }}
                label="Subscription"
                required
                disabled={subscriptionsLoading || (subscriptions.length === 0 && !subscriptionsError)}
              >
                {subscriptionsLoading ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        Loading subscriptions...
                      </Typography>
                    </Box>
                  </MenuItem>
                ) : subscriptionsError ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                      <Typography variant="body2" color="error">
                        Failed to load subscriptions
                      </Typography>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        onClick={(e) => {
                          e.stopPropagation();
                          loadSubscriptions();
                        }}
                      >
                        Retry
                      </Button>
                    </Box>
                  </MenuItem>
                ) : subscriptions.length === 0 ? (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      No subscriptions available
                    </Typography>
                  </MenuItem>
                ) : (
                  subscriptions.map((subscription) => (
                    <MenuItem key={subscription.id} value={subscription.id}>
                      {subscription.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {bulkFormErrors.subscription && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {bulkFormErrors.subscription}
                </Typography>
              )}
            </FormControl>

            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6">Users</Typography>
                  <Chip 
                    label={`${bulkUsers.length} row${bulkUsers.length !== 1 ? 's' : ''}`}
                    color="primary"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddBulkUserRow}
                  >
                    Add Row
                  </Button>
                  <Tooltip title="All the added rows will be permanently removed" arrow>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      startIcon={<ClearIcon />}
                      onClick={handleClearBulkTable}
                      disabled={bulkUsers.length <= 1}
                    >
                      Clear Table
                    </Button>
                  </Tooltip>
                </Box>
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>First Name</TableCell>
                      <TableCell>Last Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell align="center" width={80}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bulkUsers.map((user, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={user.firstName}
                            onChange={(e) => handleBulkUserChange(index, 'firstName', e.target.value)}
                            onBlur={() => {
                              // Validate on blur
                              if (!user.firstName.trim()) {
                                setBulkFormErrors(prev => ({
                                  ...prev,
                                  [`firstName_${index}`]: 'First name is required'
                                }));
                              } else if (user.firstName.trim().length < 2) {
                                setBulkFormErrors(prev => ({
                                  ...prev,
                                  [`firstName_${index}`]: 'First name must be at least 2 characters'
                                }));
                              }
                            }}
                            error={!!bulkFormErrors[`firstName_${index}`]}
                            helperText={bulkFormErrors[`firstName_${index}`]}
                            required
                            placeholder="Enter first name"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={user.lastName}
                            onChange={(e) => handleBulkUserChange(index, 'lastName', e.target.value)}
                            onBlur={() => {
                              // Validate on blur
                              if (!user.lastName.trim()) {
                                setBulkFormErrors(prev => ({
                                  ...prev,
                                  [`lastName_${index}`]: 'Last name is required'
                                }));
                              } else if (user.lastName.trim().length < 2) {
                                setBulkFormErrors(prev => ({
                                  ...prev,
                                  [`lastName_${index}`]: 'Last name must be at least 2 characters'
                                }));
                              }
                            }}
                            error={!!bulkFormErrors[`lastName_${index}`]}
                            helperText={bulkFormErrors[`lastName_${index}`]}
                            required
                            placeholder="Enter last name"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            type="email"
                            value={user.email}
                            onChange={(e) => handleBulkUserChange(index, 'email', e.target.value)}
                            onBlur={() => {
                              // Validate email on blur
                              const emailTrimmed = user.email.trim();
                              if (!emailTrimmed) {
                                setBulkFormErrors(prev => ({
                                  ...prev,
                                  [`email_${index}`]: 'Email is required'
                                }));
                              } else if (!isValidEmail(emailTrimmed)) {
                                setBulkFormErrors(prev => ({
                                  ...prev,
                                  [`email_${index}`]: 'Please enter a valid email address (e.g., user@example.com)'
                                }));
                              }
                            }}
                            error={!!bulkFormErrors[`email_${index}`]}
                            helperText={bulkFormErrors[`email_${index}`]}
                            required
                            placeholder="user@example.com"
                          />
                        </TableCell>
                        <TableCell>
                          <FormControl fullWidth size="small" error={!!bulkFormErrors[`role_${index}`]} required>
                            <Select
                              value={user.roleId}
                              onChange={(e) => {
                                handleBulkUserChange(index, 'roleId', e.target.value);
                                // Clear role error when user selects one
                                if (bulkFormErrors[`role_${index}`]) {
                                  setBulkFormErrors(prev => {
                                    const newErrors = { ...prev };
                                    delete newErrors[`role_${index}`];
                                    return newErrors;
                                  });
                                }
                              }}
                              onBlur={() => {
                                // Validate role on blur
                                if (!user.roleId || !user.roleId.trim()) {
                                  setBulkFormErrors(prev => ({
                                    ...prev,
                                    [`role_${index}`]: 'Role is required'
                                  }));
                                }
                              }}
                              displayEmpty
                              disabled={
                                !bulkSubscriptionId ||
                                bulkLoadingSubscriptionDetails ||
                                bulkFilteredRoles.length === 0
                              }
                              required
                            >
                              <MenuItem value="" disabled>
                                <em>Select role</em>
                              </MenuItem>
                              {bulkLoadingSubscriptionDetails ? (
                                <MenuItem disabled>
                                  <CircularProgress size={16} />
                                </MenuItem>
                              ) : bulkFilteredRoles.length === 0 ? (
                                <MenuItem disabled>
                                  <Typography variant="body2" color="text.secondary">
                                    {!bulkSubscriptionId ? 'Select subscription first' : 'No roles available'}
                                  </Typography>
                                </MenuItem>
                              ) : (
                                bulkFilteredRoles.map((role) => (
                                  <MenuItem key={role.id} value={role.id}>
                                    {role.name}
                                  </MenuItem>
                                ))
                              )}
                            </Select>
                          </FormControl>
                          {bulkFormErrors[`role_${index}`] && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                              {bulkFormErrors[`role_${index}`]}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveBulkUserRow(index)}
                            disabled={bulkUsers.length === 1}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={bulkSendWelcomeEmail}
                  onChange={(e) => setBulkSendWelcomeEmail(e.target.checked)}
                />
              }
              label="Send Welcome Email"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkCancel} color="secondary" disabled={bulkSubmitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleBulkSave} 
            variant="contained" 
            color="primary"
            disabled={bulkSubmitting}
          >
            {bulkSubmitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Adding...
              </Box>
            ) : (
              'Add Users'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Users;